import { Labels } from '../../global/labels';
import { Digest } from '../../global/digest';
import {
    dispatchNativeEvent,
    setInnerText,
    setNativeInputValue,
} from '../../global/ui-helpers';
import { UIElement } from '../ui-element';
import {
    FormatterType,
    formatYearsValue,
    formatMonthsValue,
    formatStep,
} from '../../global/formatters';
import { insertElements } from '../../global/render-api';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { UIHint } from '../hint/ui-hint';
import type { TranslationLabels } from '../../types/labels.type';
import type { ElementConfig } from '../ui-element.type';
import type { EventHandler, UndefinedOr } from '../../global/utils.type';
import type { CustomFormComponent } from '../../types/custom-form-component';
import styles from './ui-stepper.css';

/**
 * @class
 * @memberof SharedComponents
 * @element ui-stepper
 * @augments {UIElement}
 * @alias UIStepper
 * @classdesc Represents a class for <code>ui-stepper</code> element.
 * @implements {CustomFormComponent}
 * @property {string} [controlId] {@attr control-id} Control (input element) id. Deprecated! Prefer setting id on the native input.
 * Priority: slotted input id > control-id > generated id.
 * @property {boolean} [unlimited] {@attr unlimited} Allow put value grater than the max one.
 * @property {boolean} [labels] {@attr labels} Display hint labels for min and max values.
 * @property {FormatterType} [formatter] {@attr formatter}
 * Function to format stepper value.
 * @property {string} [units] {@attr units} Shows unit as a suffix for the values.
 * @property {HTMLInputElement} control Main input for storing model value and
 * handle events.
 * @property {number} min The minimum stepper's value (proxied from control).
 * @property {number} max The maximum stepper's value (proxied from control).
 * @property {number} step The step value of a stepper (proxied from control).
 * @property {number} value The value of the stepper (proxied from control).
 * @property {HTMLInputElement} input Shortcut to stepper value input element.
 * @property {HTMLElement|UIHint} stepperMin Shortcut to label min container.
 * @property {HTMLElement|UIHint} stepperMax Shortcut to label max container.
 * @property {HTMLDivElement} field Shortcut to input field.
 * @property {HTMLButtonElement} incrementButton Shortcut to increment button.
 * @property {HTMLButtonElement} decrementButton Shortcut to decrement button.
 * @slot
 * @example
 * <ui-stepper units="€" labels">
 *   <input type="number" min="5000" max="50000" step="1" value="28000" />
 * </ui-stepper>
 */
@Component({
    tag: 'ui-stepper',
    styles,
})
export class UIStepper extends UIElement implements CustomFormComponent {
    @Attribute(Boolean) unlimited!: boolean;
    @Attribute(Boolean) labels!: boolean;
    @Attribute(String) formatter!: FormatterType;
    @Attribute(String) units!: string;
    @Attribute(String) controlId!: string;

    @Child('.ui-stepper__control') control!: HTMLInputElement;
    @Child('.ui-stepper__input') input!: HTMLInputElement;
    @Child('.ui-stepper__field') field!: HTMLDivElement;
    @Child('.ui-stepper__button.-increment')
    incrementButton!: HTMLButtonElement;
    @Child('.ui-stepper__button.-decrement')
    decrementButton!: HTMLButtonElement;
    @Child('.ui-stepper__limit.-max') stepperMax!: HTMLElement | UIHint;
    @Child('.ui-stepper__limit.-min') stepperMin!: HTMLElement | UIHint;

    private _hasUserSetNativeId = false;
    private _id?: string;
    private focusInputHandler!: EventHandler;
    private blurInputHandler!: EventHandler;
    private enterInputHandler!: EventHandler;
    private incrementValueHandler!: EventHandler;
    private decrementValueHandler!: EventHandler;

    /**
     * @type {string[]}
     */
    static get observedAttributes(): Array<string> {
        return ['control-id'];
    }

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-stepper', {
            month: {
                1: 'month',
                2: 'months',
            },
            year: {
                1: 'year',
                2: 'years',
            },
            increase: 'Increase',
            decrease: 'Decrease',
            value: 'Value',
            max: 'Maximum',
            min: 'Minimum',
        });
    }

    /**
     * @type {UIStepperDefaults}
     * @readonly
     */
    static get Defaults(): Record<string, string> {
        return {
            Min: '0',
            Max: '10',
            Step: '1',
            Value: '0',
        };
    }

    get id(): string {
        this._id ??= Digest.uuidv4('ui-stepper-');
        return this._id;
    }

    /**
     * Returns the control id for ui-field (label[for], aria-describedby).
     * Priority: visible input id (from slotted id) > control-id > this.id.
     */
    getControlIdForField(): string {
        const controlIdAttr =
            (this.getAttribute('control-id') ?? this.controlId) || null;
        const visibleInput =
            this.input ||
            this.querySelector<HTMLInputElement>('.ui-stepper__input') ||
            null;
        if (visibleInput) {
            const inputId = visibleInput.getAttribute('id')?.trim();
            if (inputId) {
                return inputId;
            }
            if (controlIdAttr) {
                if (visibleInput.id !== controlIdAttr) {
                    visibleInput.id = controlIdAttr;
                    visibleInput.setAttribute('id', controlIdAttr);
                }
                return controlIdAttr;
            }
            const effectiveId = visibleInput.id || this.id;
            if (visibleInput.id !== effectiveId) {
                visibleInput.id = effectiveId;
                visibleInput.setAttribute('id', effectiveId);
            }
            return effectiveId;
        }
        return controlIdAttr ?? this.id;
    }

    get min(): number {
        return Number(this.control.min) || Number(UIStepper.Defaults.Min);
    }

    set min(value: string | number) {
        this.control.min = String(value);
    }

    get max(): number {
        return Number(this.control.max) || Number(UIStepper.Defaults.Max);
    }

    set max(value: string | number) {
        this.control.max = String(value);
    }

    get step(): number {
        return Number(this.control.step) || Number(UIStepper.Defaults.Step);
    }

    set step(value: string | number) {
        this.control.step = String(value);
    }

    get value(): number {
        return Number(this.control.value) || Number(UIStepper.Defaults.Value);
    }

    set value(value: string | number) {
        setNativeInputValue(this.control, String(value));
    }

    /**
     * Formats value based on given formatter.
     * @param {number} value
     * @param {boolean} [isInput]
     * @param {boolean} [noUnits]
     * @returns {string}
     */
    formatValue(value: number, isInput = false, noUnits = false): string {
        switch (this.formatter) {
            case FormatterType.Years:
                return formatYearsValue(value, {
                    noUnits,
                    labels: UIStepper.labels,
                });
            case FormatterType.Months:
                return formatMonthsValue(value, {
                    noUnits,
                    labels: UIStepper.labels,
                });
            case FormatterType.String:
                return String(value);
            default: {
                let parts: Array<string | number> = [value];
                if (this.unlimited && !isInput && value > this.max) {
                    parts = [`${this.max}+`];
                }
                if (!noUnits) {
                    parts.push(this.units);
                }
                return parts.join(' ').trim();
            }
        }
    }

    /**
     * Calculates discrete value based on the given relative value and the configured step.
     * @param {number} valueRelative
     * @returns {number} Rounded value
     */
    calcDiscreteValue(valueRelative: number): number {
        const scale = (this.max - this.min) / this.step;
        const index = Math.round(scale * valueRelative);
        return formatStep(index * this.step + this.min, { step: this.step });
    }

    /**
     * Sets the min and max values to the label placeholders.
     * @returns {UIStepper | undefined}
     */
    updateLabels(): UIStepper | void {
        if (this.labels) {
            setInnerText(this.stepperMin, this.formatValue(this.min, false));
            setInnerText(this.stepperMax, this.formatValue(this.max, false));
            this.stepperMin.ariaLabel = this.formatLabel(
                UIStepper.labels.min,
                this.min
            );
            this.stepperMax.ariaLabel = this.formatLabel(
                UIStepper.labels.max,
                this.max
            );
            return this;
        }
    }

    /**
     * @private
     * @param {Label} label
     * @param {number} value
     * @returns {string}
     */
    private formatLabel(label: TranslationLabels.Label, value: number): string {
        return `${label} ${this.formatValue(value, false)}`;
    }

    /**
     * Sets an absolute value to the controls
     * @param {number | string} value
     * @returns {UIStepper}
     */
    setValue(value: string | number): UIStepper {
        this.value = this.alignValueToRange(Number(value));

        const formattedValue = this.formatValue(this.value, true, true);
        const formattedValueWithUnits = this.formatValue(
            this.value,
            true,
            false
        );

        setNativeInputValue(this.control, formattedValue);
        this.input.value = formattedValueWithUnits;
        setInnerText(this.input, formattedValueWithUnits);

        this.toggleButtonDisabledStates();
        dispatchNativeEvent(this.control, 'change', true);
        dispatchNativeEvent(this.control, 'input', true);

        return this;
    }

    /**
     * Checks if value is in range and return an edge value if the value is out of the range.
     * @param {number} value
     * @returns {number}
     */
    alignValueToRange(value: number): number {
        const isHigherThanAllowed = value > this.max && !this.unlimited;
        const isLowerThanAllowed = value < this.min;

        if (isHigherThanAllowed) {
            return this.max;
        }
        if (isLowerThanAllowed) {
            return this.min;
        }
        return value;
    }

    /**
     * Applies the current input value to user controls.
     * @returns {UIStepper}
     */
    updateValue(): UIStepper {
        this.setValue(this.value);
        this.toggleButtonDisabledStates();
        return this;
    }

    /**
     * Toggle increment and decrement buttons disabled states based on max and min values.
     * @private
     */
    toggleButtonDisabledStates(): void {
        this.decrementButton.disabled =
            this.value === this.min || this.control.disabled;
        this.incrementButton.disabled =
            (!this.unlimited && this.value === this.max) ||
            this.control.disabled;
    }

    /**
     * Fires callback when user clicks on the decrement button.
     * @param {Event} event
     * @private
     */
    handleDecrementValue(event: Event): void {
        this.value = formatStep(this.value - this.step, { step: this.step });
        this.updateValue();
        this.returnFocusToControlAtLimit();
    }

    /**
     * Fires callback when user clicks on the increment button.
     * @param {Event} event
     * @private
     */
    handleIncrementValue(event: Event): void {
        this.value = formatStep(this.value + this.step, { step: this.step });
        this.updateValue();
        this.returnFocusToControlAtLimit();
    }

    /**
     * When reaching minimum or maximum value, focus is returned to the input.
     * @private
     */
    returnFocusToControlAtLimit(): void {
        if (
            this.value <= this.min ||
            (this.value >= this.max && !this.unlimited)
        ) {
            this.input.focus();
        }
    }

    /**
     * Fires callback when an user focuses the input element.
     * @param {Event} event
     * @private
     */
    handleFocusInput(event: Event): void {
        // Allows manually entering number values into input (which was text input with unit such as €)
        (event.target as HTMLInputElement).type = 'number';
        (event.target as HTMLInputElement).value = String(this.value);
    }

    /**
     * Fires callback when a user blurs the input element.
     * @param {Event} event
     * @private
     */
    handleBlurInput(event: Event | { target: HTMLInputElement }): void {
        const value = Number((event.target as HTMLInputElement).value);
        const shouldChangeValue =
            !this.unlimited || !this.max || value < this.max;

        this.value = shouldChangeValue
            ? this.calcDiscreteValue(this.calcProgress(value) / 100)
            : value;

        // Allows units such as € to be displayed inside input
        (event.target as HTMLInputElement).type = 'text';
        this.updateValue();
    }

    /**
     * Fires callback when a user enters vale into input.
     * @param {Event} event
     * @private
     */
    handleEnterInput(event: Event | { target: HTMLInputElement }): void {
        const enteredValue = (event.target as HTMLInputElement).value;
        this.input.value = enteredValue;
        setNativeInputValue(this.control, enteredValue);
    }

    /**
     * Calculates progress value based on the given absolute value.
     * @param {number} value
     * @returns {number}
     */
    calcProgress(value: number): number {
        if (this.unlimited && this.max && value >= this.max) {
            return 100;
        }
        return ((value - this.min) / (this.max - this.min)) * 100;
    }

    /**
     * @returns {ElementConfig} Markup of input with decrement and increment buttons.
     * Without min and max labels.
     */
    getStepperFieldEl(): ElementConfig {
        // Provided as child element of stepper component.
        const control = this.queryChildren('input')[0] as HTMLInputElement;

        // Preserve user-provided id from the slotted input for the visible input.
        // Use a prefixed id for the hidden input to avoid duplicate-id violations
        // while still allowing getElementById() to work on the visible input.
        const userProvidedId =
            control?.getAttribute('id')?.trim() ?? control?.id ?? '';
        this._hasUserSetNativeId = !!control?.getAttribute('id')?.trim();
        const baseId =
            userProvidedId ||
            (this.getAttribute('control-id') ?? this.controlId) ||
            this.id;
        const hiddenInputId = `${baseId}-hidden`;

        // Remove id from the original control element, we'll assign
        // it to the visible input and use a prefixed version for the hidden input
        if (control && (control.getAttribute('id') || control.id)) {
            control.removeAttribute('id');
            if (control.id) {
                control.id = '';
            }
        }

        const controlInput: ElementConfig = {
            tagName: 'input',
            element: control,
            attributes: {
                type: 'hidden',
                id: hiddenInputId,
            },
            classList: {
                'ui-stepper__control': true,
                '-hidden': true,
            },
        };
        const decrementButton: ElementConfig = {
            tagName: 'ui-button',
            attributes: {
                'glyph': 'redesign-minus',
                'layout': 'tertiary',
                'type': 'button',
                'aria-label': UIStepper.labels.decrease,
                'aria-controls': baseId,
                'disabled': control?.disabled,
            },
            classList: {
                'ui-stepper__button': true,
                '-decrement': true,
            },
        };
        const incrementButton: ElementConfig = {
            tagName: 'ui-button',
            attributes: {
                'glyph': 'redesign-plus',
                'layout': 'tertiary',
                'type': 'button',
                'aria-label': UIStepper.labels.increase,
                'aria-controls': baseId,
                'disabled': control?.disabled,
            },
            classList: {
                'ui-stepper__button': true,
                '-increment': true,
            },
        };
        const input: ElementConfig = {
            tagName: 'input',
            attributes: {
                'type': 'text',
                'min': control?.min || UIStepper.Defaults.Min,
                'max': control?.max || UIStepper.Defaults.Max,
                'step': control?.step || UIStepper.Defaults.Step,
                'value': control?.value || UIStepper.Defaults.Value,
                'id': baseId,
                'aria-live': 'polite',
                'readonly': control?.readOnly,
                'disabled': control?.disabled,
            },
            classList: {
                'ui-stepper__input': true,
                'ui-stepper__units-input': true,
                '-hidden': false,
            },
        };

        return {
            tagName: 'div',
            classList: {
                'ui-stepper__field': true,
            },
            children: [controlInput, decrementButton, input, incrementButton],
        };
    }

    /**
     * @returns {ElementConfig} Markup of min and max labels in case
     * they are configured to be displayed.
     */
    getMinAndMaxLabelEl(): UndefinedOr<ElementConfig> {
        if (this.labels) {
            return {
                tagName: 'div',
                classList: {
                    'ui-stepper__limits': true,
                },
                children: [
                    {
                        tagName: 'ui-hint',
                        classList: {
                            'ui-stepper__limit': true,
                            '-min': true,
                        },
                    },
                    {
                        tagName: 'ui-hint',
                        classList: {
                            'ui-stepper__limit': true,
                            '-max': true,
                        },
                    },
                ],
            };
        }
        return;
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        _oldValue: string,
        newValue: string | null
    ): void {
        if (name === 'control-id' && this.input && !this._hasUserSetNativeId) {
            if (newValue) {
                this.input.id = newValue;
                this.input.setAttribute('id', newValue);
            } else {
                this.input.removeAttribute('id');
            }
        }
    }

    /**
     * @inheritDoc
     */
    getControlElement(): HTMLInputElement {
        return this.control;
    }

    /**
     * @inheritDoc
     */
    onValuePatched(newValue: string | number): void {
        // calls event dispatch internally in setValue()
        this.setValue(newValue);
    }

    /**
     * @inheritDoc
     */
    onDisabledStateChanged(newState: boolean): void {
        this.input.disabled = newState;
        this.toggleButtonDisabledStates();
    }

    /**
     * @private
     */
    private subscribeEvents(): void {
        this.decrementButton.addEventListener(
            'click',
            this.decrementValueHandler
        );
        this.incrementButton.addEventListener(
            'click',
            this.incrementValueHandler
        );
        this.input.addEventListener('focus', this.focusInputHandler);
        this.input.addEventListener('blur', this.blurInputHandler);
        this.input.addEventListener('input', this.enterInputHandler);
    }

    /**
     * @private
     */
    private unsubscribeEvents(): void {
        this.decrementButton.removeEventListener(
            'click',
            this.decrementValueHandler
        );
        this.incrementButton.removeEventListener(
            'click',
            this.incrementValueHandler
        );
        this.input.removeEventListener('focus', this.focusInputHandler);
        this.input.removeEventListener('blur', this.blurInputHandler);
        this.input.removeEventListener('input', this.enterInputHandler);
    }

    /**
     * @inheritDoc
     */
    render(): void {
        const stepperField = this.getStepperFieldEl();
        const minAndMaxLabels = this.getMinAndMaxLabelEl();
        const elements = [stepperField];
        if (minAndMaxLabels) {
            elements.push(minAndMaxLabels);
        }
        insertElements(this, elements);
        this.updateLabels();
        this.toggleButtonDisabledStates();
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.decrementValueHandler = this.handleDecrementValue.bind(this);
        this.incrementValueHandler = this.handleIncrementValue.bind(this);
        this.focusInputHandler = this.handleFocusInput.bind(this);
        this.blurInputHandler = this.handleBlurInput.bind(this);
        this.enterInputHandler = this.handleEnterInput.bind(this);
        this.subscribeEvents();
        this.setValue(this.value);
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        this.subscribeEvents();
    }
}
